import { useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { MapPin, Filter, Play, LayoutDashboard, Users, Calendar, Trophy, Briefcase } from 'lucide-react';
import { useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';

import { DateRangePicker } from './billing/DateRangePicker';
import { BillingOverviewTab } from './billing/BillingOverviewTab';
import { BillingStreamDetail } from './billing/BillingStreamDetail';

/**
 * Billing Management
 *
 * Top-level tabs (lazy-loaded):
 *   - Overview (4 stream summary cards + grand totals)
 *   - Subscriptions
 *   - Lane Bookings
 *   - Tournaments
 *   - Coaching
 *   - Enrollment Billing (legacy detail page, linked externally)
 *
 * All tabs share the same date-range + location filters. Each stream tab only
 * loads its own items when opened, so Overview is instant and detail pages
 * stay fast even with many records.
 */
const BillingManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loadedTabs, setLoadedTabs] = useState(new Set(['overview']));

  const [dateRange, setDateRange] = useState({ period: 'current_month' });
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');

  useEffect(() => {
    api.get('/locations/active').then(r => setLocations(r.data || [])).catch(() => {});
  }, []);

  const handleTabChange = (val) => {
    setActiveTab(val);
    setLoadedTabs((prev) => new Set([...prev, val]));
  };

  const runBillingNow = async () => {
    if (!window.confirm(
      'Run billing now? This invoices every active enrollment whose next bill date has arrived (monthly/quarterly/biannual). Safe to re-run — idempotent.'
    )) return;
    try {
      const { data } = await api.post('/admin/billing/run-now');
      toast.success(
        `Billing run complete — ${data.invoices_created} invoices created, ${data.auto_charged} auto-charged${data.errors ? `, ${data.errors} errors` : ''}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run billing');
    }
  };

  return (
    <DashboardLayout title="Billing Management">
      <div className="space-y-4" data-testid="billing-management-page">
        {/* Filter bar — shared across tabs */}
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-slate-600 mr-2">Track revenue and outstanding balances by stream</p>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          {locations.length > 0 && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-slate-400" />
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="w-48" data-testid="location-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="flex items-center gap-2">
                      <Filter className="h-3 w-3" />
                      All Locations
                    </span>
                  </SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} {loc.is_primary && '(Primary)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={runBillingNow}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid="run-billing-now-btn"
              title="Generate invoices for any enrollments whose next_billing_date has arrived"
            >
              <Play className="h-4 w-4 mr-1.5" />
              Run Billing Now
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="flex w-full overflow-x-auto lg:w-auto lg:inline-flex">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <LayoutDashboard className="h-4 w-4 mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">
              <Users className="h-4 w-4 mr-1.5" /> Subscriptions
            </TabsTrigger>
            <TabsTrigger value="lane_bookings" data-testid="tab-lane-bookings">
              <Calendar className="h-4 w-4 mr-1.5" /> Lane Bookings
            </TabsTrigger>
            <TabsTrigger value="tournaments" data-testid="tab-tournaments">
              <Trophy className="h-4 w-4 mr-1.5" /> Tournaments
            </TabsTrigger>
            <TabsTrigger value="coaching" data-testid="tab-coaching">
              <Briefcase className="h-4 w-4 mr-1.5" /> Coaching
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            {loadedTabs.has('overview') && (
              <BillingOverviewTab
                dateRange={dateRange}
                locationId={selectedLocation}
                onNavigate={handleTabChange}
              />
            )}
          </TabsContent>
          <TabsContent value="subscriptions" className="mt-4">
            {loadedTabs.has('subscriptions') && (
              <BillingStreamDetail
                stream="subscriptions"
                dateRange={dateRange}
                locationId={selectedLocation}
              />
            )}
          </TabsContent>
          <TabsContent value="lane_bookings" className="mt-4">
            {loadedTabs.has('lane_bookings') && (
              <BillingStreamDetail
                stream="lane_bookings"
                dateRange={dateRange}
                locationId={selectedLocation}
              />
            )}
          </TabsContent>
          <TabsContent value="tournaments" className="mt-4">
            {loadedTabs.has('tournaments') && (
              <BillingStreamDetail
                stream="tournaments"
                dateRange={dateRange}
                locationId={selectedLocation}
              />
            )}
          </TabsContent>
          <TabsContent value="coaching" className="mt-4">
            {loadedTabs.has('coaching') && (
              <BillingStreamDetail
                stream="coaching"
                dateRange={dateRange}
                locationId={selectedLocation}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default BillingManagement;
